/**
 * Authentication API schemas
 */

import { z } from 'zod';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { Role } from '@pillar-portal/database/dist/frontend';
import { CredentialType } from '../secret.types';
import { ThirdPartySystemId } from '../types/third-party-system.enums';
import type {
    ActiveRoleAssignmentResponse,
    LoginRequest,
    LoginResponse,
    SingleUseTokensSearchRequest,
    SetDefaultRoleAssignmentRequest,
    SetDefaultRoleAssignmentUuidRequest,
    SetActiveRoleAssignmentUuidRequest,
    SessionUserProfileDetailedResponse,
    VerifyEmailResponse,
    VerifyTotpResponse,
} from '../auth.types';
import {
    PermissionGrantArraySchema,
    RoleAssignmentDetailedSchema,
} from '../permission.types';
import { CombinedConfigResponseSchema } from './samba-config.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';

/**
 * Login request
 */
export const LoginRequestSchema: z.ZodType<LoginRequest> = z.object({
    email: z.string().email(),
    password: z.string().min(1),
    payment_request_uuid: z.string().uuid().optional(),
});

/**
 * Login response
 * If requires_totp is true, user must verify TOTP before being fully authenticated
 */
export const LoginResponseSchema: z.ZodType<LoginResponse> = z.object({
    message: z.string(),
    requires_totp: z.boolean(),
    user: z
        .object({
            user_uuid: z.string().uuid(),
            primary_email: z.string(),
            emails: z.array(z.string()),
            status: z.string(),
            totp_enabled: z.boolean(),
        })
        .optional(),
});

export const SessionUserProfileDetailedResponseSchema: z.ZodType<SessionUserProfileDetailedResponse> =
    z.object({
        user_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string(),
        emails: z.array(z.string()),
        totp_enabled: z.boolean(),
        status: z.string(),
        invitation_token: z.string().optional(),
        default_role_assignment: RoleAssignmentDetailedSchema.nullable(),
        other_role_assignments: z.array(RoleAssignmentDetailedSchema),
    });

export const ActivePermissionsBundleResponseSchema: z.ZodType<
    ActiveRoleAssignmentResponse['permissions_bundle']
> = z.object({
    permissions_bundle_uuid: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    permissions: PermissionGrantArraySchema,
    is_personal: z.boolean(),
    is_active: z.boolean(),
});

export const ActiveRoleAssignmentResponseSchema: z.ZodType<ActiveRoleAssignmentResponse> = z.object({
    role_assignment_uuid: z.string().uuid(),
    organization_uuid: z.string().uuid().nullable(),
    role: z.enum(Role),
    organization_name: z.string().nullable(),
    organization_is_iso: z.boolean(),
    organization_is_merchant: z.boolean(),
    organization_is_customer: z.boolean(),
    permissions_bundle: ActivePermissionsBundleResponseSchema,
    combined_config: CombinedConfigResponseSchema,
});

export const CompleteCustomerPaymentLinkAttachmentRequestSchema = z.object({
    payment_request_uuid: z.string().uuid(),
});

/**
 * Verify TOTP request
 */
export const VerifyTotpRequestSchema = z.object({
    totp_code: z.string().min(1),
});

/**
 * Verify TOTP response
 */
export const VerifyTotpResponseSchema: z.ZodType<VerifyTotpResponse> = z.object({
    user_uuid: z.string().uuid(),
    primary_email: z.string(),
    roles: z.array(z.string()),
});

/**
 * Verify email request
 */
export const VerifyEmailRequestSchema = z.object({
    token: z.string().min(1),
    password: z.string().optional(), // Required for PENDING users
});

/**
 * Verify email response
 */
export const VerifyEmailResponseSchema: z.ZodType<VerifyEmailResponse> = z.object({
    message: z.string(),
    user: z.object({
        user_uuid: z.string().uuid(),
        primary_email: z.string().nullable(),
        status: z.string(),
    }),
});

export const CreateCustomerSelfOnboardingRequestSchema = z.object({
    email: z.string().email(),
    first_name: z.string().trim().min(1),
    last_name: z.string().trim().min(1),
    password: z.string().min(8),
    payment_request_uuid: z.string().uuid().optional(),
    store_payment_method_for_future_use: z.boolean().optional(),
});

export const CreateCustomerSelfOnboardingResponseSchema = z.object({
    message: z.string(),
    primary_email: z.string().email(),
    customer_organization_uuid: z.string().uuid(),
});

export const InvitationTokenPayloadSchema = z.object({
    primary_email: z.string().email(),
    role: z.enum(Role),
    organization_id: z.number().int().positive().nullable(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
});

export const EmailVerificationTokenPayloadSchema = z.object({
    user_id: z.number().int().positive(),
    email: z.string().email(),
});

export const PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema = z.object({
    account_id: z.string().min(1),
    credential_uuid: z.string().uuid(),
    credential_type: z.enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL]),
    is_virtual: z.boolean().nullable(),
    allow_surcharge: z.boolean(),
    allow_convenience_fee: z.boolean(),
    allow_override_fee: z.boolean(),
    surcharge_pct: z.number().nonnegative().nullable(),
    convenience_fee: z.number().nonnegative().nullable(),
});

export const PaymentRequestAccessTokenExecutionScopeSchema = z.object({
    system_id: z.literal(ThirdPartySystemId.VALOR_US),
    terminal_device_id: z.number().int().positive(),
    payment_configuration: PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema,
});

export const PaymentRequestAccessTokenPayloadSchema = z.object({
    payment_request_uuid: z.string().uuid(),
    payment_execution_scope: PaymentRequestAccessTokenExecutionScopeSchema,
});

export const SingleUseTokenListItemSchema = z.object({
    single_use_auth_token_uuid: z.string().uuid(),
    type: z.enum([
        'INVITATION',
        'PASSWORD_RESET',
        'EMAIL_VERIFICATION',
        'PAYMENT_REQUEST_ACCESS',
    ]),
    user_uuid: z.string().uuid().nullable(),
    user_primary_email: z.string().nullable(),
    email: z.string().email().nullable(),
    expire_ts: z.string().datetime(),
    used_ts: z.string().datetime().nullable(),
    is_outstanding: z.boolean(),
    is_expired: z.boolean(),
});

export const ListSingleUseTokensRequestSchema = z.object({
    outstanding_only: z.boolean(),
    q: z.string().trim().optional(),
    limit: z.number().int().positive().max(500).optional(),
    cursor: z.string().uuid().optional(),
});

export const SingleUseTokensSearchRequestSchema: z.ZodType<SingleUseTokensSearchRequest> = z
    .object({
        outstanding_only: z.boolean(),
        terms: SearchTermsSchema,
        limit: z.number().int().positive().max(500).optional(),
        cursor: z.string().uuid().optional(),
    })
    .strict();

export const ListSingleUseTokensResponseSchema = z.object({
    tokens: z.array(SingleUseTokenListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const SingleUseAuthTokenUuidParamSchema: z.ZodType<string> = z.string().uuid();

/**
 * Change password request
 */
export const ChangePasswordRequestSchema = z.object({
    old_password: z.string().min(1),
    new_password: z.string().min(1),
});

/**
 * Setup TOTP response
 */
export const SetupTotpResponseSchema = z.object({
    secret: z.string(),
    qr_code: z.string(),
    message: z.string(),
});

/**
 * Enable TOTP request
 */
export const EnableTotpRequestSchema = z.object({
    secret: z.string(),
    code: z.string().min(1),
});

/**
 * Generate backup codes response
 */
export const GenerateBackupCodesResponseSchema = z.object({
    codes: z.array(z.string()),
});

export const SetDefaultRoleAssignmentUuidRequestSchema: z.ZodType<SetDefaultRoleAssignmentUuidRequest> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
    });

/**
 * Set default role assignment request
 */
export const SetDefaultRoleAssignmentRequestSchema: z.ZodType<SetDefaultRoleAssignmentRequest> =
    SetDefaultRoleAssignmentUuidRequestSchema;

/**
 * Set active role assignment request
 */
export const SetActiveRoleAssignmentRequestSchema = z.object({
    role_assignment_id: z.number(),
});

export const SetActiveRoleAssignmentUuidRequestSchema: z.ZodType<SetActiveRoleAssignmentUuidRequest> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
    });

export const StartImpersonationRequestSchema = z.object({
    role_assignment_uuid: z.string().uuid(),
});

export const ImpersonationContextResponseSchema = z.object({
    impersonated_primary_email: z.string(),
    impersonated_role_assignment_uuid: z.string().uuid(),
    impersonated_role: z.string(),
    impersonated_organization_name: z.string().nullable(),
});

/**
 * Send verification email response
 */
export const SendVerificationEmailResponseSchema = z.object({
    message: z.string(),
    verification_link: z.string(),
});

/**
 * Request password reset request
 */
export const RequestPasswordResetRequestSchema = z.object({
    email: z.string().email(),
});

/**
 * Request password reset response
 */
export const RequestPasswordResetResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    reset_link: z.string().optional(), // For MVP manual distribution
});

export const ValidatePasswordResetTokenRequestSchema: z.ZodObject<{
    token: z.ZodString;
}> = z.object({
    token: z.string().min(1),
});

export const ValidatePasswordResetTokenResponseSchema: z.ZodObject<{
    valid: z.ZodBoolean;
    message: z.ZodString;
}> = z.object({
    valid: z.boolean(),
    message: z.string(),
});

/**
 * Reset password request
 */
export const ResetPasswordRequestSchema = z.object({
    token: z.string().min(1),
    newPassword: z.string().min(1),
});

/**
 * Reset password response
 */
export const ResetPasswordResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});

/**
 * Verify and enable TOTP response
 */
export const VerifyAndEnableTotpResponseSchema = z.object({
    message: z.string(),
    backup_codes: z.array(z.string()),
});

/**
 * Expire session admin response
 */
export const ExpireSessionAdminResponseSchema = z.object({
    message: z.string(),
    user_email: z.string().optional(),
});
